import { ArrowDown2, ArrowUp2, MinusCirlce } from 'iconsax-reactjs';
import { WidgetCard } from '@/components/WidgetCard';
import { cn } from '@/lib/utils';
import { useSymbol } from '@/lib/useSymbol';
import { useGapSummary } from './api';
import { totalGaps } from './GapSummaryGrid';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { GapIcon } from '@/components/GapIcon';

export type GapBias = 'bullish' | 'bearish' | 'neutral';

/**
 * Directional read from the unfilled gap counts.
 *
 * The premise is that an unfilled gap acts as a magnet: price tends to come back
 * and trade the band it skipped. So gaps sitting ABOVE price pull it up, and
 * gaps BELOW pull it down. More above than below therefore reads bullish.
 *
 * Note this is the opposite of the gaps' own direction labels. A gap created by
 * a bullish move (`up`) leaves its unfilled level BELOW price, because price
 * jumped away from it upward — so `up` gaps count toward a bearish read.
 *
 * Deliberately counts gaps rather than weighting by size: on gold the sizes are
 * sub-pip on fast timeframes and tens of pips on slow ones, so weighting would
 * let a single D1 gap outvote everything else without saying anything about
 * where price is actually being pulled.
 */
export function gapBias(below: number, above: number): GapBias {
  if (above > below) return 'bullish';
  if (below > above) return 'bearish';
  return 'neutral';
}

const BIAS_STYLE: Record<GapBias, { key: TranslationKey; className: string }> = {
  bullish: { key: 'common.bullish', className: 'bg-bull/15 text-bull' },
  bearish: { key: 'common.bearish', className: 'bg-bear/15 text-bear' },
  neutral: { key: 'common.neutral', className: 'bg-muted text-muted-foreground' },
};

/**
 * Compact dashboard read on gaps: two totals and the bias they imply.
 *
 * The smallest tile on the board, deliberately. Unlike the RSI readings or the
 * points queue there is no list here — the whole answer is two numbers and which
 * way they lean, so the tile is sized to that rather than padded out to match
 * its neighbours. The per-timeframe grid lives on the Gaps page.
 */
export function GapsWidget() {
  const { symbol, isLoading: symbolLoading } = useSymbol();
  const summary = useGapSummary(symbol);
  const { t } = useI18n();

  const totals = totalGaps(summary.data?.timeframes);
  const bias = gapBias(totals.below, totals.above);
  const style = BIAS_STYLE[bias];
  const loading = summary.isPending || symbolLoading;

  return (
    <WidgetCard
      to="/gaps"
      icon={<GapIcon size={16} />}
      title={t('gaps.title')}
      module="gaps"
      badge={
          loading ? (
            <span className="h-4 w-14 animate-pulse rounded bg-muted" />
          ) : (
            <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', style.className)}>
              {t(style.key)}
            </span>
          )
      }
    >

      <div className="px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Above price: a magnet pulling upward. */}
          <Count
            icon={<ArrowUp2 size={14} variant="Bold" className="text-bull" />}
            value={totals.above}
            label={t('common.above')}
            loading={loading}
          />
          <Count
            icon={<ArrowDown2 size={14} variant="Bold" className="text-bear" />}
            value={totals.below}
            label={t('common.below')}
            loading={loading}
          />
          {bias === 'neutral' && !loading && (
            <MinusCirlce size={16} className="ms-auto text-muted-foreground" />
          )}
        </div>

        {/* Names the counted frames rather than saying "all": the number and the
            verdict must describe the same set, or the tile looks wrong to
            anyone who checks it against the per-timeframe grid. */}
        <p className="mt-2.5 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
          {symbol ?? '—'} ·{' '}
          {t('gaps.widgetSummary', { count: totals.total, frames: totals.counted.join(', ') })}
        </p>
      </div>
    </WidgetCard>
  );
}

function Count({
  icon,
  value,
  label,
  loading,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  loading: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1">
        {icon}
        {loading ? (
          <span className="inline-block h-4 w-4 animate-pulse rounded bg-muted" />
        ) : (
          <span className="tabular text-base font-semibold leading-none">{value}</span>
        )}
      </div>
      <div className="mt-0.5 text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
